import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  IconButton,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import { useSnackbar } from "notistack";
import {
  MdLocalFireDepartment,
  MdWarning,
  MdDirectionsRun,
  MdShield,
  MdCrisisAlert,
  MdDirectionsCar,
  MdBroadcastOnHome,
  MdNotifications,
  MdChatBubble,
  MdLock,
  MdPhone,
  MdSearch,
  MdWater,
} from "react-icons/md";
import {
  GiPistolGun,
  GiBoltBomb,
  GiMedicalPack,
  GiAmbulance,
  GiHeavyLightning,
  GiGasMask,
  GiHazardSign,
} from "react-icons/gi";
import Content from "../../../components/Content";
import {
  GetAlarmButtonsByOrganisationId,
  DeleteAlarmButton,
} from "../../../hooks/useAlarmButtonData";
import { CreateAlarmButtonModal } from "../../../modals/general/CreateAlarmButtonModal";
import { EditAlarmButtonModal } from "../../../modals/general/EditAlarmButtonModal";
import { useOrganisation } from "../../../hooks/useOrganisation";

const INK = "#0a1628";
const INK_MUTED = "#5f6877";
const LINE = "#e6e4df";

const ICON_COMPONENT = {
  "pistol": GiPistolGun,
  "bomb": GiBoltBomb,
  "fire": MdLocalFireDepartment,
  "alert": MdWarning,
  "medical-bag": GiMedicalPack,
  "ambulance": GiAmbulance,
  "run-fast": MdDirectionsRun,
  "shield": MdShield,
  "account-alert": MdCrisisAlert,
  "car": MdDirectionsCar,
  "home-alert": MdBroadcastOnHome,
  "bell": MdNotifications,
  "chat-alert": MdChatBubble,
  "lock": MdLock,
  "phone": MdPhone,
  "magnify": MdSearch,
  "weather-lightning": GiHeavyLightning,
  "water": MdWater,
  "gas-cylinder": GiGasMask,
  "hazard-lights": GiHazardSign,
};

const ICON_LABELS = {
  "pistol": "Pistol",
  "bomb": "Sprängmedel",
  "fire": "Brand",
  "alert": "Varning",
  "medical-bag": "Sjukvård",
  "ambulance": "Ambulans",
  "run-fast": "Flykt",
  "shield": "Skydd",
  "account-alert": "Person",
  "car": "Fordon",
  "home-alert": "Hemvarning",
  "bell": "Klocka",
  "chat-alert": "Meddelande",
  "lock": "Lås",
  "phone": "Telefon",
  "magnify": "Sök",
  "weather-lightning": "Blixtar",
  "water": "Vatten",
  "gas-cylinder": "Gas",
  "hazard-lights": "Fara",
};

const AlarmIcon = ({ name, size = 22, color }) => {
  const Comp = ICON_COMPONENT[name];
  return Comp ? <Comp size={size} color={color} /> : null;
};

const ButtonCard = ({ button, onEdit, onDelete }) => (
  <Paper
    sx={{
      p: 2.5,
      borderRadius: 3,
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      display: "flex",
      alignItems: "center",
      gap: 2,
      border: `1px solid ${LINE}`,
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2,
        background: button.color,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <AlarmIcon name={button.icon} size={22} color="#fff" />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "15px",
          color: INK,
          fontFamily: '"DM Sans", sans-serif',
          mb: 0.25,
        }}
      >
        {button.label}
      </Typography>
      <Typography
        sx={{
          fontSize: "12px",
          color: INK_MUTED,
          fontFamily: '"DM Sans", sans-serif',
        }}
      >
        {ICON_LABELS[button.icon] ?? button.icon}
      </Typography>
    </Box>
    <Stack direction="row" spacing={0.5}>
      <IconButton
        size="small"
        onClick={() => onEdit(button)}
        sx={{ color: INK_MUTED, "&:hover": { color: INK } }}
      >
        <EditRoundedIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => onDelete(button)}
        sx={{ color: INK_MUTED, "&:hover": { color: "#c0392b" } }}
      >
        <DeleteRoundedIcon fontSize="small" />
      </IconButton>
    </Stack>
  </Paper>
);

export const AlarmButtonsView = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { organisationId } = useOrganisation();
  const { data: buttonsResponse, isLoading } = GetAlarmButtonsByOrganisationId(organisationId);
  const buttons = Array.isArray(buttonsResponse?.data) ? buttonsResponse.data : [];
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const deleteMutation = DeleteAlarmButton({
    onSuccess: () => enqueueSnackbar("Larmknapp borttagen.", { variant: "success" }),
    onError: () => enqueueSnackbar("Kunde inte ta bort larmknapp.", { variant: "error" }),
  });

  const handleDelete = (button) => {
    if (window.confirm(`Ta bort "${button.label}"?`)) {
      deleteMutation.mutate(button.id);
    }
  };

  return (
    <Content
      title="Larmknappar"
      subtitle="Konfigurera vilka larmknappar som visas för din organisations användare."
    >
      <Box sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <NotificationsActiveRoundedIcon sx={{ color: "#8a1125" }} />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "16px",
                color: INK,
                fontFamily: '"DM Sans", sans-serif',
              }}
            >
              Konfigurerade knappar
            </Typography>
            {!isLoading && (
              <Chip size="small" label={buttons.length} sx={{ fontWeight: 600 }} />
            )}
          </Stack>
          <Box
            component="button"
            type="button"
            onClick={() => setCreateOpen(true)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2.25,
              py: 1.25,
              background: INK,
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              fontFamily: '"DM Sans", sans-serif',
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.25s ease",
              boxShadow: "0 10px 24px -12px rgba(10,22,40,0.55)",
              "&:hover": { background: "#1a2638", transform: "translateY(-1px)" },
            }}
          >
            <AddRoundedIcon sx={{ fontSize: 18 }} />
            Ny larmknapp
          </Box>
        </Stack>

        {isLoading ? (
          <Stack spacing={1.5}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={80} sx={{ borderRadius: 3 }} />
            ))}
          </Stack>
        ) : buttons.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              px: 4,
              background: "rgba(10,22,40,0.02)",
              borderRadius: 3,
              border: `1px dashed ${LINE}`,
            }}
          >
            <NotificationsActiveRoundedIcon
              sx={{ fontSize: 40, color: LINE, mb: 2 }}
            />
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 600,
                color: INK_MUTED,
                fontFamily: '"DM Sans", sans-serif',
                mb: 1,
              }}
            >
              Inga larmknappar konfigurerade
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                color: INK_MUTED,
                fontFamily: '"DM Sans", sans-serif',
              }}
            >
              Klicka på "Ny larmknapp" för att lägga till den första.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {buttons.map((btn) => (
              <ButtonCard
                key={btn.id}
                button={btn}
                onEdit={setEditTarget}
                onDelete={handleDelete}
              />
            ))}
          </Stack>
        )}
      </Box>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontFamily: '"DM Sans", sans-serif' }}>
          Ny larmknapp
        </DialogTitle>
        <DialogContent>
          <CreateAlarmButtonModal onClose={() => setCreateOpen(false)} />
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(editTarget)}
        onClose={() => setEditTarget(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontFamily: '"DM Sans", sans-serif' }}>
          Redigera larmknapp
        </DialogTitle>
        <DialogContent>
          {editTarget && (
            <EditAlarmButtonModal
              button={editTarget}
              onClose={() => setEditTarget(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Content>
  );
};
